/**
 * Panel — the base raised surface (card / dialog backdrop) the rest of the UI kit
 * composes on. In the classic Gold/Silver look it reads as a clean cream "box": a
 * near-square rounded rect with the crisp dark G/S frame. Pure decoration; optionally
 * fills the whole safe rect. Callers may still override fill / border / radius.
 *
 * Origin is centered (0.5, 0.5) to match the layout `place()` helpers.
 */
import Phaser from 'phaser';
import { COLORS, RADIUS } from './theme.js';

export interface PanelOptions {
  readonly width: number;
  readonly height: number;
  readonly fill?: number;
  readonly border?: number;
  readonly radius?: number;
  readonly alpha?: number;
  /** Draw a soft drop-shadow behind the panel (gentle depth). */
  readonly shadow?: boolean;
}

export class Panel extends Phaser.GameObjects.Container {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private pWidth: number;
  private pHeight: number;
  private readonly opts: Required<Omit<PanelOptions, 'width' | 'height'>>;

  constructor(scene: Phaser.Scene, x: number, y: number, options: PanelOptions) {
    super(scene, x, y);
    this.pWidth = options.width;
    this.pHeight = options.height;
    this.opts = {
      fill: options.fill ?? COLORS.surface,
      border: options.border ?? COLORS.border,
      // A small radius keeps the near-square classic G/S box look (clean, not pill-y).
      radius: options.radius ?? RADIUS.md,
      alpha: options.alpha ?? 1,
      shadow: options.shadow ?? false,
    };
    this.gfx = scene.add.graphics();
    this.add(this.gfx);
    this.redraw();
    scene.add.existing(this);
  }

  /** Resize the panel (e.g. on a layout change) and redraw. */
  resize(width: number, height: number): this {
    this.pWidth = width;
    this.pHeight = height;
    this.redraw();
    return this;
  }

  get panelWidth(): number {
    return this.pWidth;
  }
  get panelHeight(): number {
    return this.pHeight;
  }

  private redraw(): void {
    const w = this.pWidth;
    const h = this.pHeight;
    const r = Math.min(this.opts.radius, w / 2, h / 2);
    const g = this.gfx;
    g.clear();
    if (this.opts.shadow) {
      g.fillStyle(COLORS.scrim, 0.25);
      g.fillRoundedRect(-w / 2 + 2, -h / 2 + 6, w, h, r);
    }
    g.fillStyle(this.opts.fill, this.opts.alpha);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    // The crisp dark G/S frame: a 2px outer line, plus a soft inner highlight line so
    // the panel reads as a classic boxed surface on the bright cream world.
    g.lineStyle(2, this.opts.border, this.opts.alpha);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    g.lineStyle(1, COLORS.boxBorderInner, this.opts.alpha * 0.7);
    g.strokeRoundedRect(-w / 2 + 3, -h / 2 + 3, w - 6, h - 6, Math.max(2, r - 3));
  }
}
